/**
 * ⛏️ Holding the button until a block gives way (T110, piece ①).
 *
 * 🎯 **haugur, 2026-08-17, choosing hold over repeated clicking:** *"aggree."*
 * **His click numbers did not change meaning** — a `click` in
 * `block-behaviour.json` is still the unit of difficulty, it is now how *long*
 * rather than how many presses, because thirty separate clicks on a grágrýti
 * block is a sore hand. `secondsToBreak` does the conversion.
 *
 * ## What this is, and what it deliberately is not
 *
 * ✅ **It is a stopwatch with rules about when to reset.** That is genuinely all
 * the mechanic is, and keeping it in one small file with no rendering and no
 * network in it means the rules can be tested without either.
 *
 * 🔴 **It is NOT server-authoritative, and that is a real gap rather than an
 * oversight.** The server accepts the break exactly as it always has — this only
 * decides *when* the client asks. A modified client could still ask instantly,
 * which is precisely what it can do today, so nothing has been made worse. But
 * `Development/CLAUDE.md` rule 6 says never trust a client packet, and the
 * honest way to close it is for the server to remember when a player started
 * breaking a block and refuse a finish that arrives too early. **That needs two
 * new protocol messages and it is tracked, not silently skipped.**
 *
 * ## 🔨 The damage belongs to the BLOCK, not to your mouse — D104
 *
 * 🎯 **haugur, 2026-09-01:** *"if you accidentally let go of the mouse button
 * you have to starta again, annoying."*
 *
 * ✅ **So letting go costs nothing, and neither does looking away.** The damage
 * you have done sits on that block while you are working on it.
 *
 * ## 🩹 But it HEALS after ten seconds — D107, and it narrows D104
 *
 * 🎯 **haugur, 2026-09-03:** *"when mining, if i accidentally hit the block next
 * to the one im braking it will crack, same with chests and hearth and crafting
 * table, that means everytime i walk by these blocks the have visible cracks,
 * thats not cool."*
 *
 * 🔴 **D104 used to say the damage sits there until somebody breaks it — "come
 * back an hour later and it is still half broken". That is no longer true**, and
 * the reason is that a permanent record of every accidental tap is *litter*: a
 * stray click on your own chest scarred it for the rest of the session.
 *
 * ✅ **Ten seconds keeps everything D104 was actually protecting.** The thing he
 * complained about in the first place — *"if you accidentally let go of the
 * mouse button you have to starta again"* — happens in a fraction of a second,
 * and re-grabbing is instant. **Walking away is what takes longer than ten
 * seconds**, and that is the case that should heal.
 *
 * ⚠️ **The clock is the LAST HIT, not the first.** Ten seconds of not being hit,
 * so a long block worked at steadily never heals under you.
 *
 * 🔴 **THIS IS NOT WHAT MINECRAFT DOES, and it was checked rather than
 * remembered.** Minecraft throws the progress and the cracks away the instant
 * the button comes up. ⚠️ **Ours is deliberately different, and the reason is
 * our own numbers:** a grágrýti block is thirty clicks against Minecraft's one
 * or two seconds, so a slip here costs ten times what a slip there costs.
 *
 * ## ⚠️ What still resets it, and why each one has to
 *
 * **The block turning into something else.** Without that, breaking a birch
 * trunk and having it replaced by falling leaves would carry ten seconds of
 * trunk progress into a one-hit leaf block.
 *
 * ⚠️ **A better tool does NOT reset it, and that is a real decision.** Damage is
 * kept as a FRACTION rather than as seconds, so swapping to an iron axe part-way
 * finishes the remaining fraction at the new speed instead of starting again.
 * Seconds would have meant a better tool sometimes *undoing* work — three
 * seconds into a ten-second block is a third done, and the same three seconds
 * against a one-second block is already past the end.
 *
 * ## 🔴 It is remembered on THIS MACHINE only, and haugur chose that
 *
 * Told plainly that the cheap version does not survive closing the game and that
 * another player would not see the cracks, he took it: *"In your own game, for
 * now."* ⚠️ **So a reload repairs every damaged block in the world**, and that
 * has to be said out loud rather than discovered. Moving it to the server is a
 * protocol change and a table, and it would also close the cheating hole
 * `Breaking` has always had.
 */

import { secondsToBreak, type Held } from '@lofi/shared';

/** Where the player is aiming, in world block coordinates. */
export interface BreakTarget {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** What is actually there, so the block changing under us resets progress. */
  readonly block: number;
}

export interface BreakProgress {
  /** 0…1. What the renderer draws. */
  readonly fraction: number;
  /** True on the frame the block should be broken. */
  readonly done: boolean;
  /** The block being broken, for the caller to send. */
  readonly target: BreakTarget;
}

function sameBlock(a: BreakTarget, b: BreakTarget): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z && a.block === b.block;
}

/**
 * Tracks one player breaking one block.
 *
 * ⚠️ **Time is passed in, never read.** A module that calls `performance.now()`
 * itself cannot be tested without faking the clock, and this project's worldgen
 * package already makes the same choice for the same reason.
 */
export class Breaking {
  private target: BreakTarget | null = null;
  private held = false;
  /**
   * 🔨 **How far each damaged block has got, 0…1.** Keyed by position, and the
   * block id is kept beside it so a block that changed under us starts again.
   *
   * ⚠️ **A fraction, not seconds** — see the note at the top of this file.
   */
  private readonly damage = new Map<string, { block: number; fraction: number; at: number }>();

  /**
   * 🕰️ **Seconds of play since this world was joined**, advanced by `update`.
   *
   * 🔴 **Not `Date.now()`, on purpose.** The class already takes `dt` and owns
   * no other clock; a wall clock would make it untestable without fake timers
   * and would keep running while the tab was asleep. ⚠️ **It advances on every
   * frame, whether or not the button is down** — healing has to happen when the
   * player is NOT breaking anything, which is the whole point of it.
   */
  private clock = 0;

  /**
   * 🩹 **How long a damaged block keeps its cracks without being hit — D107.**
   *
   * 🎯 **haugur's number, 2026-09-03:** *"if a block is hit and craks but not
   * broken within 10 seconds it looses the craxks and regains its full
   * strength."*
   *
   * ⚠️ **A playtest number**, like every other one in this game. Long enough
   * that a slipped mouse button costs nothing — the fault D104 exists to fix —
   * and short enough that a stray tap on your own chest is gone before you walk
   * back past it.
   */
  private static readonly HEALS_AFTER_SECONDS = 10;

  /**
   * 🧹 **The most damaged blocks remembered at once.**
   *
   * 🔴 **A bound rather than a guess.** The map only grows when a player starts
   * a block and walks away, which is rare — but *rare* is not *bounded*, and
   * "unmeasured is not probably fine" is a trap this project has written down.
   * At 512 entries the oldest is dropped, which repairs a block somebody
   * abandoned five hundred blocks ago and will never notice.
   */
  private static readonly REMEMBERED = 512;

  private static keyOf(t: BreakTarget): string {
    return `${t.x},${t.y},${t.z}`;
  }

  /**
   * How far along this block already is, 0…1. Zero if it is undamaged.
   *
   * 🩹 **Healed damage reads as zero and is forgotten here — D107.** Expiring on
   * the way OUT rather than sweeping the map on a timer means the cracks vanish
   * the first frame anybody looks, and a block nobody ever looks at again costs
   * nothing to leave lying until the `REMEMBERED` bound drops it.
   */
  private fractionAt(target: BreakTarget): number {
    const key = Breaking.keyOf(target);
    const seen = this.damage.get(key);
    /* 🔴 A different block in the same cell is a different thing entirely. */
    if (seen === undefined || seen.block !== target.block) return 0;
    if (this.clock - seen.at > Breaking.HEALS_AFTER_SECONDS) {
      this.damage.delete(key);
      return 0;
    }
    return seen.fraction;
  }

  /** The button went down while aiming at this. */
  start(target: BreakTarget): void {
    this.held = true;
    this.target = target;
  }

  /**
   * The button came up.
   *
   * 🔨 **Progress is KEPT — D104.** This used to throw it away, as Minecraft
   * does. 🎯 haugur: *"if you accidentally let go of the mouse button you have
   * to starta again, annoying."*
   */
  stop(): void {
    this.held = false;
    this.target = null;
  }

  /**
   * 🧹 **Forget every damaged block.**
   *
   * ⚠️ **Only for leaving a world**, and it is here rather than nowhere so that
   * the *reload repairs everything* limit is a line somebody can find and delete
   * when the damage moves to the server.
   */
  forgetDamage(): void {
    this.damage.clear();
  }

  /** How far along the block at these coordinates is, 0…1. For the renderer. */
  damageAt(x: number, y: number, z: number, block: number): number {
    return this.fractionAt({ x, y, z, block });
  }

  /**
   * Is the button down?
   *
   * ⚠️ **Deliberately not "is there a target".** The target is cleared both when
   * a block completes and when you look at the sky, and in neither case has the
   * player stopped swinging — they are still holding the button and the next
   * block under the crosshair should start taking damage on its own.
   */
  get active(): boolean {
    return this.held;
  }

  /**
   * Advance by `dt` seconds while aiming at `aiming`.
   *
   * @param aiming what the crosshair is on this frame, or null for nothing
   * @param dt seconds since the last frame
   * @param held what they are holding, or `null` for bare hands
   * @returns progress to draw, or null if nothing is being broken
   */
  update(aiming: BreakTarget | null, dt: number, held: Held = null): BreakProgress | null {
    /* 🕰️ **Before the early return, and that is the whole of D107.** Cracks
       have to heal while the player is doing something else — which is exactly
       when this method returns null on its first line. */
    this.clock += Math.max(0, dt);

    if (!this.held) return null;

    /* 🔴 **Nothing under the crosshair drops the target, and that is not the
       same as stopping.** The button is still down, so sweeping back onto a
       block picks it up again from zero — which is what a player holding the
       button through a wall of móberg expects, and what Minecraft does. */
    if (!aiming) {
      /* 🔨 Looking at the sky drops the TARGET and keeps the DAMAGE. The button
         is still down, so sweeping back onto the block picks it up where it was
         left rather than at zero. */
      this.target = null;
      return null;
    }

    /* A new block, or a different one: just aim at it. Whatever damage it
       already carries is looked up below, so there is nothing to reset. */
    if (!this.target || !sameBlock(this.target, aiming)) {
      this.target = aiming;
    }

    const needed = secondsToBreak(this.target.block, held);
    /* Bedrock and anything else unbreakable: aimed at, never damaged. */
    if (needed === null) return null;

    const key = Breaking.keyOf(this.target);
    /* 🔨 **A fraction of the way through, not a number of seconds.** Adding
       `dt / needed` is what lets a better tool finish what your hands started
       instead of restarting it — the remaining fraction simply passes faster. */
    const fraction = Math.min(1, this.fractionAt(this.target) + Math.max(0, dt) / needed);
    const done = fraction >= 1;

    const progress: BreakProgress = { fraction, done, target: this.target };

    if (done) {
      /* The block is gone, so its damage is meaningless. ⚠️ Keep the button
         held, so holding down on a wall of móberg breaks one block after
         another rather than needing a click each time. */
      this.damage.delete(key);
      this.target = null;
    } else {
      /* ⚠️ Delete before setting, so re-inserting an existing key moves it to
         the END of the Map's insertion order — otherwise the oldest-out
         eviction below would drop the block somebody is working on right now. */
      this.damage.delete(key);
      /* 🩹 `at` is stamped on every hit, so the ten seconds run from the LAST
         one — a block worked at steadily never heals under the player. */
      this.damage.set(key, { block: this.target.block, fraction, at: this.clock });
      if (this.damage.size > Breaking.REMEMBERED) {
        const oldest = this.damage.keys().next();
        if (!oldest.done) this.damage.delete(oldest.value);
      }
    }

    return progress;
  }
}
